import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';

import * as c from 'utils/fieldConstants';
import { fetchCampaignsQuery, fetchCampaignQuery } from './MultiSelectDropdown.queries';
import { formatResponse, getCampaignResultsFromData } from './MultiSelectDropdown.utils';
import { BaseSelectedOptionsProps } from './MultiSelectDropdown.types';

const FETCH_POLICY = 'cache-and-network';

type ResultItem = {
  id: number;
  name: string;
};

export const useGetCampaignsSelectOptions = ({
  defaultOptions,
  field,
  values = [],
}: BaseSelectedOptionsProps) => {
  const [options, setOptions] = useState(defaultOptions);
  const [hasError, setHasError] = useState(false);

  const [selectedCampaigns, setSelectedCampaigns] = useState({});
  const [campaigns, setNonselectedCampaigns] = useState<any[]>([]);
  const [fetchQueue, setFetchQueue] = useState<any[]>([]);

  /* -------------------- order and set functions --------------------*/
  const formatAndSetOptions = (results: ResultItem[]) => {
    const formattedOptions = formatResponse(results);
    setOptions(formattedOptions);
  };

  const fetchAndReorderOptions = (results: ResultItem[]) => {
    const campaignsResults: ResultItem[] = [...results];
    const nonFetched: number[] = [];
    let fetched = {};

    values.forEach((item) => {
      const campaignId = Number(item);
      const idx = campaignsResults.findIndex((opt) => opt.id === campaignId);

      if (idx > -1) {
        const selected = campaignsResults.splice(idx, 1);
        fetched = {
          ...fetched,
          [campaignId]: selected[0],
        };
      } else {
        nonFetched.push(campaignId);
      }
    });

    // use campaign data if we already have it
    setSelectedCampaigns(fetched);
    // fetch campaigns that aren't in the initial query
    setFetchQueue(nonFetched);
    // store everything else
    setNonselectedCampaigns(campaignsResults);
  };

  const updateFetchedCampaigns = (data: Record<number, any>) => {
    setSelectedCampaigns((addtlCampaigns) => ({
      ...addtlCampaigns,
      ...data,
    }));
    setFetchQueue((fq) => fq.slice(1));
  };

  /* -------------------- queries --------------------*/
  const [getCampaign, { loading: campaignLoading }] = useLazyQuery(fetchCampaignQuery, {
    fetchPolicy: FETCH_POLICY,
    onError: () => {
      // we should still display the rest of the campaigns if this fails
      updateFetchedCampaigns({
        [fetchQueue[0]]: false,
      });
      setHasError(true);
    },
    onCompleted: (data) => {
      updateFetchedCampaigns({
        [data?.campaign?.id]: data?.campaign,
      });
      setHasError(false);
    },
  });

  const [getCampaigns, { loading: campaignsLoading, variables }] = useLazyQuery(
    fetchCampaignsQuery,
    {
      fetchPolicy: FETCH_POLICY,
      onError: () => setHasError(true),
      variables: {
        search: '',
        campaignFilterInfo: {
          // this excludes the parent recurring campaigns by not including 'Recurring'
          campaignState: ['Draft', 'Finished', 'Ready', 'Running', 'Scheduled'],
        },
      },
      onCompleted: (data) => {
        const results = getCampaignResultsFromData(data);
        if (variables?.search || !values.length) {
          formatAndSetOptions(results);
        } else {
          fetchAndReorderOptions(results);
        }
        setHasError(false);
      },
    },
  );

  /* ---------- useEffects for handling single fetched campaigns ---------- */
  useEffect(() => {
    const addtlCampaigns = Object.values(selectedCampaigns);
    if (values.length && addtlCampaigns.length === values.length) {
      formatAndSetOptions([...addtlCampaigns.filter((item) => !!item).reverse(), ...campaigns]);
    }
  }, [selectedCampaigns, campaigns]);

  useEffect(() => {
    if (fetchQueue.length) {
      getCampaign({
        variables: { campaignId: fetchQueue[0] },
      });
    }
  }, [fetchQueue]);

  /* -------------------- configuration -------------------- */
  const CAMPAIGNS_CONFIG = {
    query: getCampaigns,
    isLoading: campaignsLoading || campaignLoading,
    fieldName: 'campaigns',
  };
  const fieldConfigMap: any = {
    [c.CAMPAIGN]: CAMPAIGNS_CONFIG,
    [c.RECURRING_CAMPAIGN]: CAMPAIGNS_CONFIG,
  };

  const runQuery = (opts: any) => {
    const query = fieldConfigMap[field]?.query;
    if (query) {
      query(opts);
    }
  };

  return {
    hasError,
    options,
    runQuery,
    isLoading: fieldConfigMap[field]?.isLoading,
    fieldName: fieldConfigMap[field]?.fieldName,
  };
};
